import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MSG } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Send, MessageSquare, Loader2, Image as ImageIcon, Paperclip, Mic,
  Square, Phone, X, AlertOctagon, FileText, PlayCircle, Reply, Smile, HeartHandshake
} from "lucide-react";
import SOSButton from "@/components/SOSButton";

const EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

function VoiceRecorder({ onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        onRecorded(blob, seconds);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };
  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  if (!recording) {
    return (
      <button type="button" onClick={start} data-testid="messages-voice-start"
        className="p-2 rounded-full text-stone2-600 hover:bg-stone2-100" title="Record voice note">
        <Mic className="w-4 h-4" />
      </button>
    );
  }
  return (
    <button type="button" onClick={stop} data-testid="messages-voice-stop"
      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-danger text-white text-xs">
      <Square className="w-3 h-3" /> {seconds}s
    </button>
  );
}

function EmergencyPanel({ otherUserId, otherRole, myRole, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Fetch emergency info of the FAMILY in the conversation
        const familyUserId = myRole === "family" ? null : otherUserId;
        if (!familyUserId) {
          setData({ error: "You are the family in this conversation — your emergency contacts appear in your profile." });
          return;
        }
        const { data } = await api.get(`/families/${familyUserId}/emergency`);
        setData(data);
      } catch (e) {
        setData({ error: extractError(e) });
      } finally { setLoading(false); }
    };
    load();
  }, [otherUserId, myRole]);

  return (
    <div className="border-b border-danger/30 bg-danger/5 p-4" data-testid="messages-emergency-panel">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2">
          <AlertOctagon className="w-5 h-5 text-danger mt-0.5" />
          <div>
            <div className="font-heading font-semibold text-danger">Emergency contacts</div>
            <p className="text-xs text-stone2-600">Use for urgent situations only.</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded hover:bg-stone2-100"
          data-testid="messages-emergency-close">
          <X className="w-4 h-4 text-stone2-500" />
        </button>
      </div>
      {loading && <div className="mt-3 text-sm text-stone2-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
      {data?.error && <p className="mt-3 text-sm text-stone2-600">{data.error}</p>}
      {data && !data.error && (
        <div className="mt-3 space-y-2">
          {data.parent_phone && (
            <a href={`tel:${data.parent_phone}`} data-testid="messages-emergency-parent-call"
              className="flex items-center justify-between rounded-lg bg-white border border-danger/20 p-3 hover:border-danger/40 transition-colors duration-200">
              <div>
                <div className="text-xs uppercase tracking-wider text-stone2-500 font-semibold">Parent</div>
                <div className="text-sage-900 font-semibold text-sm">{data.parent_name || data.family_name || "Family"}</div>
                <div className="text-xs text-stone2-600">{data.parent_phone}</div>
              </div>
              <span className="btn-primary text-xs"><Phone className="w-3.5 h-3.5" /> Call</span>
            </a>
          )}
          {(data.emergency_contacts || []).map((c, i) => (
            <a key={`${c.phone || ""}-${c.name || ""}-${i}`} href={`tel:${c.phone}`} data-testid={`messages-emergency-contact-${i}`}
              className="flex items-center justify-between rounded-lg bg-white border border-danger/20 p-3 hover:border-danger/40 transition-colors duration-200">
              <div>
                <div className="text-xs uppercase tracking-wider text-stone2-500 font-semibold">{c.relationship}</div>
                <div className="text-sage-900 font-semibold text-sm">{c.name}</div>
                <div className="text-xs text-stone2-600">{c.phone}</div>
              </div>
              <span className="btn-primary text-xs"><Phone className="w-3.5 h-3.5" /> Call</span>
            </a>
          ))}
          {data.address && (
            <div className="rounded-lg bg-white border border-stone2-200 p-3 text-sm text-stone2-700">
              <div className="text-xs uppercase tracking-wider text-stone2-500 font-semibold">Home address</div>
              {data.address}, {data.city} {data.state} {data.zip_code}
            </div>
          )}
          {data.medical_info && (
            <div className="rounded-lg bg-white border border-stone2-200 p-3 text-sm text-stone2-700" data-testid="messages-emergency-medical">
              <div className="text-xs uppercase tracking-wider text-stone2-500 font-semibold">Medical info</div>
              <div className="whitespace-pre-line">{data.medical_info}</div>
            </div>
          )}
          {data.local_emergency_instructions && (
            <div className="rounded-lg bg-white border border-stone2-200 p-3 text-sm text-stone2-700" data-testid="messages-emergency-local">
              <div className="text-xs uppercase tracking-wider text-stone2-500 font-semibold">Local emergency instructions</div>
              <div className="whitespace-pre-line">{data.local_emergency_instructions}</div>
            </div>
          )}
          <div className="pt-2">
            <SOSButton testId="messages-sos-inline" />
          </div>
        </div>
      )}
    </div>
  );
}

function Attachment({ a }) {
  const url = `${BACKEND_URL}/api/uploads/${a.path}`;
  if (a.kind === "photo" || (a.content_type || "").startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`attachment-${a.path}`}>
        <img src={url} alt="" className="mt-2 rounded-lg max-w-[220px] max-h-[220px] object-cover border border-stone2-200" />
      </a>
    );
  }
  if (a.kind === "voice" || (a.content_type || "").startsWith("audio/")) {
    return (
      <div className="mt-2 flex items-center gap-2" data-testid={`attachment-${a.path}`}>
        <PlayCircle className="w-4 h-4 text-sage-600" />
        <audio controls src={url} className="max-w-[240px]" />
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer"
      data-testid={`attachment-${a.path}`}
      className="mt-2 inline-flex items-center gap-2 rounded-lg border border-stone2-200 bg-white px-3 py-2 text-xs text-sage-800 hover:border-sage-300">
      <FileText className="w-3.5 h-3.5" />
      {a.filename || "File"} <span className="text-stone2-500">({Math.round((a.size || 0) / 1024)} KB)</span>
    </a>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTo = params.get("to");
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(initialTo || null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [activeOther, setActiveOther] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [reactPickerFor, setReactPickerFor] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [sitterPreview, setSitterPreview] = useState(null);
  const [maskedForViewer, setMaskedForViewer] = useState(false);
  const scrollRef = useRef(null);
  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadThreads = async () => {
    try { const { data } = await api.get("/messages/threads"); setThreads(data.threads); }
    catch (e) { toast.error(extractError(e)); }
  };

  const loadThread = async (otherId) => {
    if (!otherId) return;
    try {
      const { data } = await api.get(`/messages/thread/${otherId}`);
      setMessages(data.messages);
      setConversation(data.conversation || null);
      setSitterPreview(data.sitter_preview || null);
      setMaskedForViewer(!!data.masked_for_viewer);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    } catch (e) { toast.error(extractError(e)); }
  };

  const acceptConversation = async () => {
    if (!conversation?.id) return;
    try {
      await api.post(`/conversations/${conversation.id}/accept`);
      toast.success("You can now see this sitter's full profile");
      await loadThread(activeId);
      await loadThreads();
    } catch (e) { toast.error(extractError(e)); }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (activeId) {
      loadThread(activeId);
      const t = threads.find((x) => x.other_user_id === activeId);
      setActiveOther(t || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Poll fallback (WS often blocked by preview proxy)
  useEffect(() => {
    const iv = setInterval(() => {
      if (activeId) loadThread(activeId);
      loadThreads();
    }, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const uploadFile = async (file, kind) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("is_public", "false");
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return {
        kind,
        path: data.path,
        filename: file.name || `voice-${Date.now()}`,
        content_type: data.content_type,
        size: file.size,
      };
    } catch (e) { toast.error(extractError(e)); return null; }
    finally { setUploading(false); }
  };

  const sendWithAttachment = async (attachment, prefill = "") => {
    try {
      await api.post("/messages", {
        to_user_id: activeId,
        body: prefill,
        attachments: [attachment],
      });
      loadThread(activeId);
      loadThreads();
    } catch (err) { toast.error(extractError(err)); }
  };

  const handlePhotoChosen = async (e) => {
    const f = e.target.files?.[0]; if (!f || !activeId) return;
    const att = await uploadFile(f, "photo");
    if (att) await sendWithAttachment(att);
    e.target.value = "";
  };

  const handleFileChosen = async (e) => {
    const f = e.target.files?.[0]; if (!f || !activeId) return;
    const att = await uploadFile(f, "file");
    if (att) await sendWithAttachment(att);
    e.target.value = "";
  };

  const handleVoiceRecorded = async (blob, seconds) => {
    if (!activeId) return;
    const f = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
    const att = await uploadFile(f, "voice");
    if (att) {
      att.duration_seconds = seconds;
      await sendWithAttachment(att);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!activeId || !body.trim()) return;
    setSending(true);
    try {
      const payload = { to_user_id: activeId, body };
      if (replyTo) payload.reply_to_message_id = replyTo.id;
      await api.post("/messages", payload);
      setBody("");
      setReplyTo(null);
      loadThread(activeId);
      loadThreads();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 402 && d?.code === "card_required") {
        if (window.confirm(`${d.message}\n\nSave a payment method now?`)) {
          try {
            const { data } = await api.post("/billing/setup-card", { origin_url: window.location.origin });
            window.location.href = data.url;
          } catch (e2) { toast.error(extractError(e2)); }
        }
      } else if (err?.response?.status === 402 && d?.code === "card_declined") {
        toast.error(d.message || "Card declined. Try another card.");
      } else if (err?.response?.status === 402 && d?.code === "upgrade_required") {
        if (window.confirm(`${d.message}\n\nGo to Pricing?`)) window.location.href = "/pricing";
      } else if (err?.response?.status === 402 && d?.code === "conversation_upgrade_required") {
        if (window.confirm(`${d.message}\n\nSee subscription plans?`)) window.location.href = d.upgrade_url || "/settings/subscription";
      } else {
        toast.error(extractError(err));
      }
    }
    finally { setSending(false); }
  };

  const react = async (msgId, emoji) => {
    try {
      await api.post(`/messages/${msgId}/react`, { emoji });
      setReactPickerFor(null);
      loadThread(activeId);
    } catch (err) { toast.error(extractError(err)); }
  };

  const findMessage = (id) => messages.find((m) => m.id === id);

  return (
    <div className="min-h-screen bg-stone2-50" data-testid={MSG.page}>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid md:grid-cols-3 gap-4 h-[calc(100vh-8rem)]">
        <aside className="paper-card p-3 overflow-y-auto" data-testid={MSG.threadList}>
          <h2 className="font-heading font-semibold text-sage-900 px-2 py-1 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Conversations
          </h2>
          {threads.length === 0 && <p className="text-sm text-stone2-500 p-3">No conversations yet.</p>}
          <ul className="mt-2 space-y-1">
            {threads.map((t) => (
              <li key={t.other_user_id}>
                <button type="button" onClick={() => setActiveId(t.other_user_id)}
                  data-testid={MSG.thread(t.other_user_id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                    activeId === t.other_user_id ? "bg-sage-50 border border-sage-200" : "hover:bg-stone2-50"
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sage-900 text-sm">{t.other_name}</div>
                    {t.unread > 0 && <span className="chip-ochre">{t.unread}</span>}
                  </div>
                  <div className="text-xs text-stone2-500 truncate mt-0.5">{t.last_message}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="md:col-span-2 paper-card flex flex-col overflow-hidden">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center text-stone2-500 text-sm">
              Select a conversation to start messaging.
            </div>
          ) : (
            <>
              <div className="border-b border-stone2-200 p-3 flex items-center justify-between">
                <div>
                  {user?.role === "sitter" && activeOther?.other_role === "family" && activeOther?.other_user_id ? (
                    <Link to={`/family/${activeOther.other_user_id}/view`}
                      data-testid="messages-view-family-profile"
                      className="font-heading font-semibold text-sage-900 text-sm hover:text-sage-600 hover:underline">
                      {activeOther?.other_name || "Conversation"}
                    </Link>
                  ) : (
                    <div className="font-heading font-semibold text-sage-900 text-sm">
                      {activeOther?.other_name || "Conversation"}
                    </div>
                  )}
                  <div className="text-xs text-stone2-500 capitalize">{activeOther?.other_role || ""}</div>
                </div>
                <button type="button" onClick={() => setShowEmergency((s) => !s)}
                  data-testid="messages-emergency-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-danger text-white text-xs font-semibold px-3 py-1.5 hover:bg-red-700 transition-colors duration-200">
                  <AlertOctagon className="w-3.5 h-3.5" /> Emergency
                </button>
              </div>

              {showEmergency && (
                <EmergencyPanel
                  otherUserId={activeId}
                  otherRole={activeOther?.other_role}
                  myRole={user?.role}
                  onClose={() => setShowEmergency(false)}
                />
              )}

              {/* Sitter view: pending family acceptance */}
              {conversation?.state === "pending" && user?.role === "sitter" && (
                <div className="border-b border-stone2-200 bg-stone2-50 px-4 py-2.5 text-xs text-stone2-600" data-testid="pending-for-sitter">
                  Waiting for the family to accept. They see your message anonymously until they reply.
                </div>
              )}

              {/* Pending-accept banner (family side only) */}
              {maskedForViewer && sitterPreview && conversation?.state === "pending" && (
                <div className="border-b border-ochre-200 bg-ochre-50 p-4" data-testid="pending-accept-banner">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-ochre-500 text-white flex items-center justify-center shrink-0 font-bold">
                      ?
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-sage-900 text-sm">A specialized sitter reached out</div>
                      <div className="text-xs text-stone2-600 mt-1">
                        Their name and photo stay private until you accept.
                      </div>
                      {sitterPreview.elevator_pitch && (
                        <blockquote className="mt-3 border-l-2 border-ochre-500 pl-3 text-sm text-sage-900 italic" data-testid="sitter-pitch-quote">
                          "{sitterPreview.elevator_pitch}"
                        </blockquote>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                        {sitterPreview.years_experience != null && (
                          <span className="chip">{sitterPreview.years_experience} yr exp</span>
                        )}
                        {sitterPreview.hourly_rate != null && (
                          <span className="chip">${sitterPreview.hourly_rate}/hr</span>
                        )}
                        {sitterPreview.aba_trained && <span className="chip-ochre">ABA</span>}
                        {(sitterPreview.certifications || []).slice(0, 4).map((c) => (
                          <span key={c} className="chip">{c}</span>
                        ))}
                        {sitterPreview.city && (
                          <span className="chip">{sitterPreview.city}{sitterPreview.state ? ", " + sitterPreview.state : ""}</span>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" onClick={acceptConversation}
                          data-testid="accept-conversation-btn"
                          className="btn-primary text-sm">
                          Accept and reply
                        </button>
                        <span className="text-xs text-stone2-500 self-center">
                          Or type a reply below — sending will auto-accept.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((m) => {
                  const mine = m.from_user_id === user?.id;
                  const parent = m.reply_to_message_id ? findMessage(m.reply_to_message_id) : null;
                  // Aggregate reactions by emoji
                  const reactionGroups = (m.reactions || []).reduce((acc, r) => {
                    acc[r.emoji] = acc[r.emoji] || { emoji: r.emoji, users: [] };
                    acc[r.emoji].users.push(r.user_name);
                    return acc;
                  }, {});
                  return (
                    <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"} relative`}
                      data-testid={`message-${m.id}`}>
                      {/* Hover actions */}
                      <div className={`opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center gap-1 self-center ${
                        mine ? "order-first mr-1" : "ml-1 order-last"
                      }`}>
                        <button type="button" onClick={() => setReplyTo(m)}
                          data-testid={`message-reply-${m.id}`}
                          title="Reply"
                          className="p-1.5 rounded-full bg-white border border-stone2-200 text-stone2-600 hover:text-sage-800 hover:border-sage-300">
                          <Reply className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => setReactPickerFor(reactPickerFor === m.id ? null : m.id)}
                          data-testid={`message-react-${m.id}`}
                          title="React"
                          className="p-1.5 rounded-full bg-white border border-stone2-200 text-stone2-600 hover:text-ochre-600 hover:border-ochre-300">
                          <Smile className="w-3 h-3" />
                        </button>
                        {reactPickerFor === m.id && (
                          <div className={`absolute z-10 flex gap-1 rounded-full bg-white shadow-md border border-stone2-200 p-1 ${
                            mine ? "right-16 top-0" : "left-16 top-0"
                          }`} data-testid={`message-react-picker-${m.id}`}>
                            {EMOJIS.map((e) => (
                              <button key={e} type="button" onClick={() => react(m.id, e)}
                                data-testid={`message-react-emoji-${m.id}-${e}`}
                                className="text-lg hover:scale-125 transition-transform duration-100">
                                {e}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col max-w-[75%]">
                        {parent && (
                          <div className={`mb-1 text-[11px] rounded-lg px-3 py-1.5 border-l-2 ${
                            mine ? "border-sage-200 bg-sage-50 text-sage-800 self-end" : "border-stone2-300 bg-stone2-50 text-stone2-700 self-start"
                          }`}
                            data-testid={`message-reply-preview-${m.id}`}>
                            <span className="font-semibold">{parent.from_name}: </span>
                            <span className="italic">{(parent.body || "[attachment]").slice(0, 80)}</span>
                          </div>
                        )}

                        <div className={`px-4 py-2 rounded-2xl text-sm ${
                          mine ? "bg-sage-600 text-white rounded-br-sm" : "bg-stone2-100 text-sage-900 rounded-bl-sm"
                        }`}>
                          {m.body && <div>{m.body}</div>}
                          {(m.attachments || []).map((a, i) => <Attachment key={a.path || a.url || a.name || `att-${m.id}-${i}`} a={a} />)}
                          <div className={`text-[10px] mt-1 ${mine ? "text-sage-100" : "text-stone2-500"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>

                        {Object.keys(reactionGroups).length > 0 && (
                          <div className={`mt-1 flex flex-wrap gap-1 ${mine ? "self-end" : "self-start"}`}
                            data-testid={`message-reactions-${m.id}`}>
                            {Object.values(reactionGroups).map((g) => (
                              <button key={g.emoji} type="button" onClick={() => react(m.id, g.emoji)}
                                title={g.users.join(", ")}
                                data-testid={`message-reaction-chip-${m.id}-${g.emoji}`}
                                className="inline-flex items-center gap-0.5 rounded-full bg-white border border-stone2-200 px-2 py-0.5 text-xs hover:border-ochre-300">
                                <span>{g.emoji}</span>
                                <span className="text-stone2-600 font-semibold">{g.users.length}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <p className="text-center text-stone2-400 text-sm">Say hello 👋</p>
                )}
              </div>

              {replyTo && (
                <div className="border-t border-stone2-200 bg-stone2-50 px-3 py-2 flex items-start justify-between gap-2" data-testid="messages-reply-composer">
                  <div className="text-xs">
                    <div className="font-semibold text-sage-800">Replying to {replyTo.from_name}</div>
                    <div className="italic text-stone2-600 line-clamp-1">{replyTo.body || "[attachment]"}</div>
                  </div>
                  <button type="button" onClick={() => setReplyTo(null)}
                    data-testid="messages-reply-cancel"
                    className="p-1 rounded hover:bg-stone2-100">
                    <X className="w-4 h-4 text-stone2-500" />
                  </button>
                </div>
              )}

              <form onSubmit={send} className="border-t border-stone2-200 p-3 flex items-center gap-2">
                <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChosen} />
                <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.csv" className="hidden" onChange={handleFileChosen} />
                <button type="button" onClick={() => photoInputRef.current?.click()}
                  data-testid="messages-attach-photo" title="Photo"
                  className="p-2 rounded-full text-stone2-600 hover:bg-stone2-100">
                  <ImageIcon className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  data-testid="messages-attach-file" title="File"
                  className="p-2 rounded-full text-stone2-600 hover:bg-stone2-100">
                  <Paperclip className="w-4 h-4" />
                </button>
                <VoiceRecorder onRecorded={handleVoiceRecorded} />

                <input value={body} onChange={(e) => setBody(e.target.value)} placeholder={uploading ? "Uploading…" : "Write a message..."}
                  data-testid={MSG.composer} disabled={uploading}
                  className="flex-1 rounded-full border border-stone2-200 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-sage-500 outline-none disabled:opacity-60" />
                <button type="submit" disabled={sending || uploading || !body.trim()} data-testid={MSG.send}
                  className="btn-primary text-sm">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
